import { useCallback, useEffect, useState } from "react";
import { DecisionTree } from "../../types/api/decision-tree";
import { get, set } from "idb-keyval";
import api from "../../utils/api/axios";
import { AxiosError } from "axios";
import { setHasNoNetwork } from "../../store/reducers/auth.slice";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import "./DecisionTree.css";
import { StyledCircularProgress } from "../../components/Loader";
import TreeSelect from "../../components/decision-tree/TreeSelect";
import { useNavigate } from "react-router-dom";
import { DecisionTreeType } from "../../types/api/decision-tree";

interface DecisionTreeSelectProps {
  treeType: DecisionTreeType;
}

const DecisionTreeSelect = ({ treeType }: DecisionTreeSelectProps) => {
  const navigate = useNavigate();
  const [decisionTreeList, setDecisionTreeList] = useState<DecisionTree[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const auth = useAppSelector((state) => state.auth);
  const { hasNoNetwork } = auth;
  const dispatch = useAppDispatch();

  const fetchDataOffline = useCallback(async () => {
    const decisionTrees = await get<DecisionTree[]>(
      `decision-trees-${treeType}`
    );
    if (!decisionTrees) return false;
    setDecisionTreeList(decisionTrees);
    return true;
  }, [treeType]);

  const saveDecisionTrees = async (
    decisionTrees: DecisionTree[],
    treeType: DecisionTreeType
  ) => {
    await set(`decision-trees-${treeType}`, decisionTrees);
  };

  const fetchDataOnline = useCallback(async () => {
    const { data } = await api.get<DecisionTree[]>(`/decision-trees/list`, {
      params: {
        treeType,
      },
    });

    setDecisionTreeList(data.sort((a, b) => a.name.localeCompare(b.name)));
    saveDecisionTrees(data, treeType);
  }, [treeType]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const offlineSuccess = await fetchDataOffline();
    if (offlineSuccess) {
      setIsLoading(false);
    }
    try {
      await fetchDataOnline();
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status === 500) dispatch(setHasNoNetwork(true));
      }
    }
    setIsLoading(false);
  }, [dispatch, fetchDataOffline, fetchDataOnline]);

  useEffect(() => {
    fetchData();
  }, [fetchData, hasNoNetwork, treeType]);

  const handleSelectDecisionTree = useCallback(
    (tree: DecisionTree) => {
      navigate(
        `/${
          treeType === "ADMINISTRATION"
            ? "aide-administration"
            : "aide-prescription"
        }/${tree.id}`
      );
    },
    [navigate, treeType]
  );

  return (
    <div className="decison-tree-container">
      <h1 className="decision-tree-title">
        {treeType === "ADMINISTRATION"
          ? "Aide à l'administration des antibiotiques"
          : "Aide à la prescription des antibiotiques"}
      </h1>
      {isLoading ? (
        <StyledCircularProgress size={24} />
      ) : (
        <div className="decision-tree-select-container">
          <p className="decision-tree-select-text">
            Sélectionner une{" "}
            {treeType === "PRESCRIPTION" ? "infection" : "molécule"} dans la
            liste ci-dessous pour accéder à l'antibiothérapie appropriée
          </p>
          <TreeSelect
            decisionTreeList={decisionTreeList}
            onSelectDecisionTree={handleSelectDecisionTree}
            decistionTreeType={treeType}
          />
        </div>
      )}
    </div>
  );
};

export default DecisionTreeSelect;
